import { resolveInvoiceStatus } from "./invoices";
import { resolveInvoiceDueDate } from "./invoicePaymentTerms";

export const INVOICE_COLLECTION_STATUS = Object.freeze({
  PAID: "paid",
  CURRENT: "current",
  DUE_SOON: "due_soon",
  OVERDUE: "overdue",
});

export const DUE_SOON_THRESHOLD_DAYS = 7;

export function getDaysUntilDue(invoice, asOfDate = new Date()) {
  const status = resolveInvoiceStatus(invoice?.status);
  if (status !== "sent") {
    return null;
  }

  const dueDateValue = resolveInvoiceDueDate(invoice);
  if (!dueDateValue) {
    return null;
  }

  const due = new Date(`${dueDateValue}T00:00:00.000Z`);
  const asOf = new Date(asOfDate);
  due.setUTCHours(0, 0, 0, 0);
  asOf.setUTCHours(0, 0, 0, 0);

  const diffMs = due.getTime() - asOf.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

export function getInvoiceCollectionStatus(invoice, asOfDate = new Date()) {
  const status = resolveInvoiceStatus(invoice?.status);

  if (status === "paid") {
    return INVOICE_COLLECTION_STATUS.PAID;
  }

  if (status !== "sent") {
    return null;
  }

  const daysUntilDue = getDaysUntilDue(invoice, asOfDate);
  if (daysUntilDue === null) {
    return null;
  }

  if (daysUntilDue < 0) {
    return INVOICE_COLLECTION_STATUS.OVERDUE;
  }

  if (daysUntilDue <= DUE_SOON_THRESHOLD_DAYS) {
    return INVOICE_COLLECTION_STATUS.DUE_SOON;
  }

  return INVOICE_COLLECTION_STATUS.CURRENT;
}

export function formatDaysUntilDueLabel(invoice, asOfDate = new Date()) {
  const status = resolveInvoiceStatus(invoice?.status);
  if (status === "paid") {
    return "Paid in full";
  }

  if (status !== "sent") {
    return null;
  }

  const daysUntilDue = getDaysUntilDue(invoice, asOfDate);
  if (daysUntilDue === null) {
    return null;
  }

  if (daysUntilDue < 0) {
    const overdueDays = Math.abs(daysUntilDue);
    return overdueDays === 1 ? "1 day overdue" : `${overdueDays} days overdue`;
  }

  if (daysUntilDue === 0) {
    return "Due today";
  }

  if (daysUntilDue === 1) {
    return "Due tomorrow";
  }

  return `Due in ${daysUntilDue} days`;
}

export function formatInvoiceCollectionStatusLabel(
  collectionStatus,
  invoice,
  asOfDate = new Date(),
) {
  switch (collectionStatus) {
    case INVOICE_COLLECTION_STATUS.PAID:
      return "Paid";
    case INVOICE_COLLECTION_STATUS.CURRENT:
      return "Current";
    case INVOICE_COLLECTION_STATUS.DUE_SOON:
      return "Due Soon";
    case INVOICE_COLLECTION_STATUS.OVERDUE:
      return "Overdue";
    default:
      return formatDaysUntilDueLabel(invoice, asOfDate) || "";
  }
}

export function getInvoiceCollectionStatusPillStyle(collectionStatus) {
  const base = {
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    display: "inline-block",
  };

  switch (collectionStatus) {
    case INVOICE_COLLECTION_STATUS.PAID:
      return {
        ...base,
        border: "1px solid rgba(78,226,161,0.45)",
        background: "rgba(78,226,161,0.12)",
        color: "#4ee2a1",
      };
    case INVOICE_COLLECTION_STATUS.CURRENT:
      return {
        ...base,
        border: "1px solid rgba(201,168,76,0.45)",
        background: "rgba(201,168,76,0.12)",
        color: "#c9a84c",
      };
    case INVOICE_COLLECTION_STATUS.DUE_SOON:
      return {
        ...base,
        border: "1px solid rgba(250,204,21,0.45)",
        background: "rgba(250,204,21,0.12)",
        color: "#facc15",
      };
    case INVOICE_COLLECTION_STATUS.OVERDUE:
      return {
        ...base,
        border: "1px solid rgba(224,107,90,0.55)",
        background: "rgba(224,107,90,0.16)",
        color: "#e06b5a",
      };
    default:
      return {
        ...base,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(255,255,255,0.06)",
        color: "rgba(255,255,255,0.82)",
      };
  }
}

export function shouldShowInvoiceDueInfo(invoice) {
  const status = resolveInvoiceStatus(invoice?.status);
  return status === "sent" || status === "paid";
}
